import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, FileCheck2, ChevronDown } from "lucide-react";
import { waLink, CATEGORIES } from "../data/office";
import { TRIAGE, COMMON_QUESTIONS, URGENT, CLIENT_STATUS } from "../data/triage";

const inputCls =
  "w-full rounded-lg border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-cream placeholder:text-mist/50 outline-none transition-colors focus:border-gold-500";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-widest text-mist";

const isVisible = (q, values) =>
  (!q.showIf || values[q.showIf.id] === q.showIf.equals) &&
  !(q.hideIf && values[q.hideIf.id] === q.hideIf.equals);

const urgentReasons = (questions, values) =>
  questions
    .filter((q) => q.urgentIf && isVisible(q, values) && q.urgentIf[values[q.id]])
    .map((q) => q.urgentIf[values[q.id]]);

const collect = (questions, values) =>
  questions
    .filter((q) => isVisible(q, values) && values[q.id] && String(values[q.id]).trim())
    .map((q) => [q.short || q.label.replace(/[?:]$/, ""), String(values[q.id]).trim()]);

function Question({ q, value, onChange }) {
  return (
    <div>
      <p className={labelCls}>{q.label}</p>
      {q.type === "choice" ? (
        <div className="flex flex-wrap gap-2">
          {q.options.map((opt) => {
            const on = value === opt;
            return (
              <button
                key={opt}
                type="button"
                data-testid={`triage-${q.id}-option`}
                onClick={() => onChange(on ? "" : opt)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  on
                    ? "border-gold-500 bg-gold-500 font-semibold text-navy-950"
                    : "border-navy-600 text-mist hover:border-gold-500/60 hover:text-cream"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={q.placeholder}
          className={inputCls}
        />
      )}
      {q.notices && q.notices[value] && (
        <p className="mt-3 rounded-lg border border-gold-500/40 bg-gold-500/10 px-4 py-3 text-xs leading-relaxed text-cream">
          {q.notices[value]}
        </p>
      )}
    </div>
  );
}

export default function TriageModal({ solution, onClose }) {
  const [pickedArea, setPickedArea] = useState(null);
  const areaSlug = solution?.area || pickedArea;
  const area = areaSlug ? TRIAGE[areaSlug] : null;
  const generic = !solution?.area;
  const areaLabel = CATEGORIES.find((c) => c.slug === areaSlug)?.label;
  const title = solution?.title || (areaLabel ? areaLabel : "Agendar consulta");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [summary, setSummary] = useState("");
  const [answers, setAnswers] = useState({});
  const [common, setCommon] = useState({});
  const [consent, setConsent] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  // Cada novo assunto começa com as perguntas em branco
  useEffect(() => {
    setAnswers({});
    setShowDocs(false);
  }, [title]);

  useEffect(() => {
    if (!solution) setPickedArea(null);
    else if (solution.prefill) setSummary(solution.prefill);
  }, [solution]);

  useEffect(() => {
    if (!solution) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [solution, onClose]);

  const open = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
    onClose();
  };

  const isCaseQ = (q) => q.id === URGENT.id;
  const triageAnswers = [
    ...(area ? collect(area.questions, answers) : []),
    ...collect(COMMON_QUESTIONS.filter(isCaseQ), common),
  ];
  const contactAnswers = collect(COMMON_QUESTIONS.filter((q) => !isCaseQ(q)), common);
  const reasons = [
    ...(area ? urgentReasons(area.questions, answers) : []),
    ...urgentReasons(COMMON_QUESTIONS, common),
  ];
  const urgent = reasons.length > 0;
  const deadline = common[URGENT.id] === URGENT.equals;

  const docs = area
    ? [
        ...new Set([
          ...area.documents,
          ...(area.docRules || [])
            .filter((r) => {
              const q = area.questions.find((x) => x.id === r.id);
              return !q || isVisible(q, answers);
            })
            .flatMap((r) => r.map[answers[r.id]] || []),
        ]),
      ]
    : [];
  const docsPending = area && area.docRules && area.docRules.length && !answers[area.docRules[0].id];

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));
  const setCommonAnswer = (id, value) => setCommon((a) => ({ ...a, [id]: value }));

  return (
    <AnimatePresence>
      {solution && (
        <motion.div
          data-testid="triage-modal"
          className="fixed inset-0 z-50 flex items-end justify-center bg-navy-950/80 p-4 backdrop-blur-sm sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Triagem para agendamento"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gold-500/25 bg-navy-800 shadow-2xl"
          >
            <div className="h-1 w-full shrink-0 gold-rule" />
            <button
              data-testid="modal-close-button"
              onClick={onClose}
              aria-label="Fechar"
              className="absolute right-4 top-4 z-10 rounded-full p-2 text-mist transition-colors hover:bg-navy-700 hover:text-cream"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="overflow-y-auto overscroll-contain p-7 sm:p-9" data-lenis-prevent>
              <p className="font-mono-meta text-[10px] uppercase tracking-[0.3em] text-gold-400">
                Triagem rápida · WhatsApp
              </p>
              <h3 className="font-display mt-3 pr-8 text-3xl font-semibold leading-tight text-cream">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                Responda o que souber — leva menos de 1 minuto e agiliza muito o seu atendimento. Nenhum campo é
                obrigatório.
              </p>

              {generic && (
                <div className="mt-7">
                  <p className={labelCls}>Qual é o assunto?</p>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((c) => {
                      const on = pickedArea === c.slug;
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          data-testid={`triage-area-${c.slug}`}
                          onClick={() => setPickedArea(on ? null : c.slug)}
                          className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                            on
                              ? "border-gold-500 bg-gold-500 font-semibold text-navy-950"
                              : "border-navy-600 text-mist hover:border-gold-500/60 hover:text-cream"
                          }`}
                        >
                          {c.label.replace(/^Direito (do |de )?/, "")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {area && (
                <div className="mt-7 space-y-5">
                  {area.questions
                    .filter((q) => isVisible(q, answers))
                    .map((q) => (
                      <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                    ))}
                </div>
              )}

              <div className="mt-7 space-y-5 border-t border-navy-700 pt-6">
                {COMMON_QUESTIONS.filter((q) => isVisible(q, common)).map((q) => (
                  <Question key={q.id} q={q} value={common[q.id]} onChange={(v) => setCommonAnswer(q.id, v)} />
                ))}
                {deadline && (
                  <p className="rounded-lg border border-gold-500/40 bg-gold-500/10 px-4 py-3 text-xs leading-relaxed text-cream">
                    Sua mensagem será marcada como <strong>urgente</strong>. Se puder, envie no WhatsApp uma foto da
                    intimação ou do documento recebido.
                  </p>
                )}
              </div>

              <div className="mt-6 space-y-4 border-t border-navy-700 pt-6">
                <div>
                  <label htmlFor="triage-name" className={labelCls}>
                    Nome
                  </label>
                  <input
                    id="triage-name"
                    data-testid="triage-input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome completo"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="triage-phone" className={labelCls}>
                    Seu WhatsApp / telefone de contato
                  </label>
                  <input
                    id="triage-phone"
                    data-testid="triage-input-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(35) 99999-9999"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="triage-summary" className={labelCls}>
                    Conte brevemente o que aconteceu
                  </label>
                  <textarea
                    id="triage-summary"
                    data-testid="triage-input-summary"
                    rows={3}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="Descreva em poucas linhas"
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              {area && (
                <div className="mt-6 rounded-xl border border-gold-500/25 bg-navy-900/60">
                  <button
                    type="button"
                    data-testid="triage-docs-toggle"
                    onClick={() => setShowDocs((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-semibold text-cream"
                  >
                    <span className="flex items-center gap-2.5">
                      <FileCheck2 className="h-4 w-4 text-gold-400" />
                      Documentos para separar
                    </span>
                    <ChevronDown className={`h-4 w-4 text-mist transition-transform ${showDocs ? "rotate-180" : ""}`} />
                  </button>
                  {showDocs && (
                    <ul data-testid="triage-docs-list" className="space-y-2 px-5 pb-5 text-sm text-mist">
                      {docsPending && (
                        <li className="text-xs text-gold-400">
                          Responda as perguntas acima para ver os documentos do seu caso.
                        </li>
                      )}
                      {docs.map((d) => (
                        <li key={d} className="flex gap-2.5">
                          <span className="mt-2 h-1 w-1 shrink-0 rotate-45 bg-gold-500" />
                          {d}
                        </li>
                      ))}
                      <li className="pt-1 text-xs text-mist/70">
                        Não tem tudo? Sem problema — envie o que tiver e orientamos o restante.
                      </li>
                    </ul>
                  )}
                </div>
              )}

              <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-mist">
                <input
                  type="checkbox"
                  data-testid="triage-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37]"
                />
                <span>
                  Autorizo o envio destes dados ao escritório para análise do meu caso, conforme a{" "}
                  <a
                    href="/privacidade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 underline underline-offset-2"
                  >
                    Política de Privacidade
                  </a>
                  .
                </span>
              </label>

              <button
                data-testid="triage-submit-button"
                disabled={!consent}
                onClick={() =>
                  open(
                    waLink(title, {
                      area: areaLabel,
                      triage: triageAnswers,
                      contact: contactAnswers,
                      place: common.unidade || common.cidade_cliente,
                      status: CLIENT_STATUS[common.cliente],
                      name: name.trim(),
                      phone: phone.trim(),
                      summary: summary.trim(),
                      urgent,
                      reasons,
                    })
                  )
                }
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold-500 py-4 text-sm font-bold uppercase tracking-widest text-navy-950 transition-all duration-300 hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gold-500"
              >
                <MessageCircle className="h-4 w-4" />
                Enviar e ir ao WhatsApp
              </button>
              <button
                data-testid="triage-skip-button"
                onClick={() => open(waLink(title))}
                className="mt-3 w-full py-3 text-center text-sm font-medium text-mist underline-offset-4 transition-colors hover:text-gold-400 hover:underline"
              >
                Pular e ir direto ao WhatsApp
              </button>
              <p className="mt-4 text-center text-[11px] leading-relaxed text-mist/60">
                Nada é armazenado neste site — as respostas vão apenas na mensagem que você envia ao WhatsApp do
                escritório.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
